import { resolveEventName } from "./event-names";
import { eventClassLabel, eventClassOf, isPublicClass } from "./event-state";
import { countryName } from "./geo";
import { eventsTheaterPath } from "./route";
import { CLAIM, IGNIS_ORIGIN } from "./sources";
import type { EventStatus, IncidentDetail } from "./types";

/** Facts we need for a theater <title> / description — no heat, no series. */
export type EventSeo = {
  id: string;
  place_name: string | null;
  country: string;
  lat: number;
  lon: number;
  first_seen: string;
  last_seen: string;
  peak_frp_mw: number;
  event_status: EventStatus | null;
  live: boolean;
};

export type SeoPage = "map" | "events" | "theater" | "privacy";

export type PageMeta = {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  robots: string;
  jsonLd: unknown;
};

export type SitemapEntry = { id: string; last_seen: string };

/** Home share-card copy — keep in sync with web/index.html so Slack cards do not flip. */
export const MAP_TITLE = "IGNIS - Wildfires in Europe Tracker";
export const MAP_DESCRIPTION = "Near-real-time satellite-observed fire activity across Europe.";
export const MAP_OG_TITLE = "IGNIS";
export const MAP_OG_DESCRIPTION =
  "Integrated Geospatial Network for Ignition Surveillance. Near-Real-Time Satellite-Observed Fire Activity for Europe.";

export const EVENTS_TITLE = "IGNIS - Wildfires in Europe Tracker";
export const EVENTS_DESCRIPTION =
  "Catalogued major satellite-observed wildfires across Europe — live campaigns and archive snapshots.";

export const PRIVACY_TITLE = "Privacy — IGNIS";
export const PRIVACY_DESCRIPTION = "How IGNIS uses your location and email.";

const OG_IMAGE_ALT =
  "IGNIS: Integrated Geospatial Network for Ignition Surveillance. Near-Real-Time Satellite-Observed Fire Activity for Europe.";

export function ogImageUrl(origin = IGNIS_ORIGIN): string {
  return `${origin.replace(/\/+$/, "")}/og.png`;
}

export function ogImageAlt(): string {
  return OG_IMAGE_ALT;
}

/** Titles, descriptions, canonical, JSON-LD for one app route. Filter query strings stay off the canonical. */
export function pageMeta(input: {
  page: SeoPage;
  origin: string;
  noindex?: boolean;
  event?: EventSeo | null;
}): PageMeta {
  const origin = input.origin.replace(/\/+$/, "");
  const robots = input.noindex ? "noindex, nofollow" : "index, follow";
  if (input.page === "privacy") {
    return {
      title: PRIVACY_TITLE,
      description: PRIVACY_DESCRIPTION,
      canonical: `${origin}/privacy`,
      ogTitle: PRIVACY_TITLE,
      ogDescription: PRIVACY_DESCRIPTION,
      robots,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: PRIVACY_TITLE,
        url: `${origin}/privacy`,
        isPartOf: { "@type": "WebApplication", name: "IGNIS", url: `${origin}/` },
      },
    };
  }
  // Map sidebar and /events theater share the same tab title; canonical stays the theater URL.
  if (input.event) {
    const event = input.event;
    const title = eventTitle(event);
    const description = eventDescription(event);
    const url = `${origin}${eventsTheaterPath(event.id)}`;
    return {
      title,
      description,
      canonical: url,
      ogTitle: title,
      ogDescription: description,
      robots,
      jsonLd: eventJsonLd(origin, event, title, description, url),
    };
  }
  if (input.page === "events" || input.page === "theater") {
    return {
      title: EVENTS_TITLE,
      description: EVENTS_DESCRIPTION,
      canonical: `${origin}/events`,
      ogTitle: EVENTS_TITLE,
      ogDescription: EVENTS_DESCRIPTION,
      robots,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: EVENTS_TITLE,
        url: `${origin}/events`,
        description: EVENTS_DESCRIPTION,
        isPartOf: { "@type": "WebApplication", name: "IGNIS", url: `${origin}/` },
      },
    };
  }
  return {
    title: MAP_TITLE,
    description: MAP_DESCRIPTION,
    canonical: `${origin}/`,
    ogTitle: MAP_OG_TITLE,
    ogDescription: MAP_OG_DESCRIPTION,
    robots,
    jsonLd: mapJsonLd(origin),
  };
}

export function mapJsonLd(origin: string): unknown {
  const url = `${origin.replace(/\/+$/, "")}/`;
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "IGNIS",
    alternateName: "Integrated Geospatial Network for Ignition Surveillance",
    url,
    description: CLAIM,
    applicationCategory: "WeatherApplication",
    operatingSystem: "Any",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    areaServed: { "@type": "Place", name: "Europe" },
    image: ogImageUrl(origin),
  };
}

function eventJsonLd(origin: string, event: EventSeo, name: string, description: string, url: string): unknown {
  const start = Date.parse(event.first_seen);
  const end = Date.parse(event.last_seen);
  const temporal =
    Number.isFinite(start) && Number.isFinite(end)
      ? `${new Date(start).toISOString()}/${new Date(end).toISOString()}`
      : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name,
    description,
    url,
    isPartOf: { "@type": "CollectionPage", url: `${origin}/events` },
    about: { "@type": "Thing", name: "Wildfire" },
    spatialCoverage: {
      "@type": "Place",
      name: eventPlace(event),
      geo: { "@type": "GeoCoordinates", latitude: event.lat, longitude: event.lon },
    },
    ...(temporal ? { temporalCoverage: temporal, dateCreated: new Date(start).toISOString() } : {}),
    keywords: eventKeywords(event),
    creator: { "@type": "Organization", name: "IGNIS", url: `${origin}/` },
  };
}

export function eventPlace(event: Pick<EventSeo, "id" | "place_name" | "country">): string {
  return resolveEventName(event.id, event.place_name) || countryName(event.country) || "Unknown";
}

const SEO_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SEO_MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** UTC calendar date so the tab title does not change with the visitor's timezone. */
export function seoUtcDate(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return `${d.getUTCDate()} ${SEO_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function seoUtcMonth(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return `${SEO_MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Tab title: news name, country, kind, month. Same on the map sidebar and /events. */
export function eventTitle(event: EventSeo): string {
  const name = eventPlace(event);
  const country = countryName(event.country);
  const labeled = country && !name.toLowerCase().endsWith(country.toLowerCase()) ? `${name}, ${country}` : name;
  const month = seoUtcMonth(event.first_seen);
  return month ? `${labeled} - Wildfire - ${month}` : `${labeled} - Wildfire`;
}

function eventKeywords(event: EventSeo): string {
  const year = seoUtcDate(event.first_seen).slice(-4);
  return [eventPlace(event), countryName(event.country), "wildfire", year, event.live ? "live" : "archive"]
    .filter(Boolean)
    .join(", ");
}

/** Crawler / share blurb — richer than the tab, still not shown on the page. */
export function eventDescription(event: EventSeo): string {
  const country = countryName(event.country);
  const place = eventPlace(event);
  const where = event.place_name && country && event.place_name !== country ? `${place}, ${country}` : place;
  const first = seoUtcDate(event.first_seen);
  const last = seoUtcDate(event.last_seen);
  const cls = eventClassOf(event.event_status);
  const classBit = isPublicClass(cls) ? eventClassLabel(cls) : "";
  const peak = Number.isFinite(event.peak_frp_mw) ? `${Math.round(event.peak_frp_mw)} MW` : "";
  const span =
    first && last && first !== last ? `First look ${first}. Last look ${last}.` : first ? `First look ${first}.` : "";
  const life = event.live ? "Still on the live map." : "Archive snapshot — no longer on the live map.";
  return [
    `Satellite-observed wildfire near ${where}.`,
    span,
    classBit ? `${classBit}.` : "",
    peak ? `Peak ${peak}.` : "",
    life,
  ]
    .filter(Boolean)
    .join(" ");
}

export function eventSeoFromDetail(detail: IncidentDetail, peakFrpMw?: number): EventSeo {
  return {
    id: detail.incident.id,
    place_name: resolveEventName(detail.incident.id, detail.place_name),
    country: detail.incident.country,
    lat: detail.incident.lat,
    lon: detail.incident.lon,
    first_seen: detail.incident.first_seen,
    last_seen: detail.incident.last_seen,
    peak_frp_mw: peakFrpMw ?? detail.incident.frp_mw,
    event_status: detail.incident.event_status ?? null,
    live: !detail.archive,
  };
}

/** Production lists the sitemap; staging tells every bot to stay out. */
export function robotsTxt(input: { origin: string; noindex: boolean }): string {
  if (input.noindex) {
    return "User-agent: *\nDisallow: /\n";
  }
  const origin = input.origin.replace(/\/+$/, "");
  return [
    "User-agent: *",
    "Allow: /",
    "Disallow: /api/",
    "Disallow: /unsubscribe",
    "",
    `Sitemap: ${origin}/sitemap.xml`,
    "",
  ].join("\n");
}

/** Home, catalog, privacy, then each public catalogued fire. */
export function sitemapXml(origin: string, events: SitemapEntry[]): string {
  const base = origin.replace(/\/+$/, "");
  const newest = events.reduce((best, e) => (e.last_seen > best ? e.last_seen : best), "");
  const urls = [
    sitemapUrl(`${base}/`, newest),
    sitemapUrl(`${base}/events`, newest),
    sitemapUrl(`${base}/privacy`),
    ...events.map((e) => sitemapUrl(`${base}${eventsTheaterPath(e.id)}`, e.last_seen)),
  ];
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls.join("\n")}\n</urlset>\n`;
}

function sitemapUrl(loc: string, lastSeen?: string): string {
  const lastmod = lastSeen ? sitemapDate(lastSeen) : "";
  const extra = lastmod ? `\n    <lastmod>${lastmod}</lastmod>` : "";
  return `  <url>\n    <loc>${xmlEscape(loc)}</loc>${extra}\n  </url>`;
}

export function sitemapDate(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  return new Date(t).toISOString().slice(0, 10);
}

export function xmlEscape(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" })[ch]!);
}
